import React, { useState } from 'react';
import { signatureApi } from '../lib/api';
import { Button } from './ui/button';
import { toast } from 'sonner';

/**
 * Where the signatory's approval code is entered.
 *
 * The stored signature is a legal instrument, so a letter only prints signed
 * once the person it belongs to has approved this session. The code goes to
 * the signatory's own inbox — never to whoever is operating the portal.
 *
 * Props:
 *   sentTo   masked address the code was emailed to, e.g. an***@aampl.in
 *   onDone   (signToken) => void   — approved
 *   onSkip   ()          => void   — print unsigned instead
 *   onClose  ()          => void
 */
export default function SignatureApprovalDialog({ sentTo, onDone, onSkip, onClose }) {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [dest, setDest] = useState(sentTo || '');

  const verify = async () => {
    if (code.trim().length !== 6) { toast.error('Enter the 6-digit code'); return; }
    setBusy(true);
    try {
      const r = await signatureApi.verifyOtp(code.trim());
      toast.success('Signature approved');
      onDone(r.data.sign_token);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not verify the code'); }
    finally { setBusy(false); }
  };

  const resend = async () => {
    setBusy(true);
    try {
      const r = await signatureApi.requestOtp();
      setDest(r.data.sent_to);
      setCode('');
      toast.success(`New code sent to ${r.data.sent_to}`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not resend'); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
         role="dialog" aria-modal="true" data-testid="signature-approval-dialog">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-5">
        <h3 className="text-base font-semibold text-[#11182B]">Signature approval required</h3>
        <p className="text-[12px] text-[#5A6377] mt-1.5 mb-4 leading-relaxed">
          A 6-digit code has been emailed to the signatory at <b>{dest}</b>.
          Enter it below to print this letter <b>with the authorised signature</b>.
        </p>

        <input
          value={code}
          autoFocus
          inputMode="numeric"
          maxLength={6}
          data-testid="signature-approval-code"
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
          onKeyDown={(e) => e.key === 'Enter' && verify()}
          placeholder="······"
          className="w-full border border-[#E6E9F0] rounded-md px-3 py-3 text-center text-2xl tracking-[0.45em] font-bold focus:outline-none focus:border-[#2563EB]"
        />

        <div className="flex items-center justify-between mt-4">
          <button onClick={resend} disabled={busy}
            className="text-xs text-[#2563EB] hover:underline disabled:opacity-50">
            Resend code
          </button>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={onClose}>Cancel</Button>
            <Button size="sm" onClick={verify} disabled={busy || code.length !== 6}>
              {busy ? 'Checking…' : 'Approve & print'}
            </Button>
          </div>
        </div>

        <div className="border-t border-[#F2F4F8] mt-4 pt-3">
          <button onClick={onSkip} className="text-xs text-[#5A6377] hover:underline">
            Print without the signature (for wet signing)
          </button>
        </div>
      </div>
    </div>
  );
}
